/**
 * Email Service - Frontend API Client
 * 
 * This service handles all email-related API calls to the backend.
 * It tracks user behavior and triggers email campaigns.
 */

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';

// Helper: Get user email from Firestore users collection
// Phone/MSG91 users don't have email in Firebase Auth, only in Firestore
async function getUserEmailFromFirestore(userId) {
  try {
    const { getFirestore, doc, getDoc } = await import('firebase/firestore');
    const { default: firebaseApp } = await import('../firebase/firebaseConfig.js');
    const db = getFirestore(firebaseApp);
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data().email || '';
    }
    return '';
  } catch (err) {
    console.warn('Could not fetch email from Firestore:', err.message);
    return '';
  }
}

class EmailService {
  /**
   * Send a welcome email to a new user
   * Should be called after user signs up or logs in for the first time
   * 
   * @param {string} userId - Firebase user ID
   * @param {string} email - User's email address
   * @param {string} name - User's display name
   * @returns {Promise<Object>} Response from the backend
   */
  async sendWelcomeEmail(userId, email, name) {
    try {
      // Resolve email from Firestore if not available (phone/MSG91 users)
      let resolvedEmail = email;
      if (!resolvedEmail && userId) {
        resolvedEmail = await getUserEmailFromFirestore(userId);
      }
      if (!resolvedEmail) {
        console.log('⚠️ No email available for welcome email — skipping');
        return { success: false, error: 'No email set' };
      }

      console.log('Sending welcome email request:', { userId, email: resolvedEmail, name });
      
      const response = await fetch(`${API_BASE_URL}/api/email/welcome`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          email: resolvedEmail,
          name
        })
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response headers:', response.headers);

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Expected JSON but got:', contentType);
        console.error('❌ Response body:', text.substring(0, 200));
        return { success: false, error: 'Server returned non-JSON response' };
      }

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Failed to send welcome email:', data.error);
        return { success: false, error: data.error };
      }

      console.log('✅ Welcome email sent successfully');
      return data;
    } catch (error) {
      console.error('Error sending welcome email:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Track when a user visits the pricing page
   * This is used to trigger abandoned cart emails if they don't purchase
   * 
   * @param {string} userId - Firebase user ID
   * @param {string} email - User's email address
   * @param {string} name - User's display name
   * @param {number} timeSpentSeconds - Time spent on pricing page in seconds
   * @returns {Promise<Object>} Response from the backend
   */
  async trackPricingPageVisit(userId, email, name, timeSpentSeconds = 0) {
    try {
      // Resolve email from Firestore if not available
      let resolvedEmail = email;
      if (!resolvedEmail && userId) {
        resolvedEmail = await getUserEmailFromFirestore(userId);
      }
      if (!resolvedEmail) {
        console.log('No email available for pricing visit tracking — skipping');
        return { success: true, message: 'No email set' };
      }

      const response = await fetch(`${API_BASE_URL}/api/email/pricing-visit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          email: resolvedEmail,
          name,
          timeSpentSeconds
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Failed to track pricing page visit:', data.error);
        return { success: false, error: data.error };
      }

      console.log('✅ Pricing page visit tracked');
      return data;
    } catch (error) {
      console.error('Error tracking pricing page visit:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Manually trigger an abandoned cart email (for testing)
   * In production, this should be handled by a scheduled job on the backend
   * 
   * @param {string} userId - Firebase user ID
   * @returns {Promise<Object>} Response from the backend
   */
  async sendAbandonmentEmail(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/email/pricing-abandonment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Failed to send abandonment email:', data.error);
        return { success: false, error: data.error };
      }

      console.log('✅ Abandonment email sent successfully');
      return data;
    } catch (error) {
      console.error('Error sending abandonment email:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Notify backend of a payment failure so a declined email is sent to the user
   * @param {string} userId
   * @param {string} email
   * @param {string} name
   */
  async notifyPaymentFailed(userId, email, name) {
    try {
      // Resolve email from Firestore if not available
      let resolvedEmail = email;
      if (!resolvedEmail && userId) {
        resolvedEmail = await getUserEmailFromFirestore(userId);
      }
      if (!resolvedEmail) {
        console.log('No email available for payment failed notification — skipping');
        return { success: false, error: 'No email set' };
      }

      const response = await fetch(`${API_BASE_URL}/api/payment/payment-failed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, email: resolvedEmail, name })
      });
      const data = await response.json();
      console.log('✅ Payment failed notification sent');
      return data;
    } catch (error) {
      console.error('Error sending payment failed notification:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send payment abandonment email (user visited pricing page but left without buying)
   * @param {string} userId
   * @param {string} email
   * @param {string} name
   */
  async sendPaymentAbandonmentEmail(userId, email, name) {
    try {
      // Resolve email from Firestore if not available
      let resolvedEmail = email;
      if (!resolvedEmail && userId) {
        resolvedEmail = await getUserEmailFromFirestore(userId);
      }
      if (!resolvedEmail) {
        console.log('No email available for payment abandonment — skipping');
        return { success: false, error: 'No email set' };
      }

      const response = await fetch(`${API_BASE_URL}/api/email/payment-abandonment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, email: resolvedEmail, name })
      });
      const data = await response.json();
      console.log('✅ Payment abandonment email triggered');
      return data;
    } catch (error) {
      console.error('Error sending payment abandonment email:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Mark a purchase as completed (prevents abandoned cart email)
   * Should be called immediately after successful payment
   * 
   * @param {string} userId - Firebase user ID
   * @param {string} planType - Type of plan purchased (silver, gold, platinum)
   * @returns {Promise<Object>} Response from the backend
   */
  async markPurchaseCompleted(userId, planType) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/email/purchase-completed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          planType
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.error('Failed to mark purchase as completed:', data.error);
        return { success: false, error: data.error };
      }

      console.log('✅ Purchase marked as completed');
      return data;
    } catch (error) {
      console.error('Error marking purchase as completed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Verify email service configuration (for testing)
   * 
   * @returns {Promise<Object>} Email service status
   */
  async verifyEmailService() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/email/verify`, {
        method: 'GET'
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error verifying email service:', error);
      return { success: false, error: error.message };
    }
  }
}

// Export singleton instance
export default new EmailService();
